import {
  useCallback,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react'
import { useTranslation } from 'react-i18next'
import { FileText, Pencil, X } from 'lucide-react'
import { formatDocumentFileType } from '@/lib/constants'
import { formatDate } from '@/lib/format'
import {
  fileNameBase,
  fileNameExtension,
  validateDocumentRename,
} from '@/lib/documentRename'
import { getApiFieldError } from '@/services/api'
import { cn } from '@/lib/utils'
import { TruncatedTextTooltip } from './TruncatedTextTooltip'
import type { AttachmentRef } from './types'

const NAME_MAX = 150

// eslint-disable-next-line react-refresh/only-export-components
export function formatAttachmentSize(bytes: number): string {
  if (bytes >= 1024 * 1024) {
    return `${Math.round(bytes / (1024 * 1024))} MB`
  }
  if (bytes >= 1024) {
    return `${Math.round(bytes / 1024)} KB`
  }
  return `${bytes} B`
}

function isImageRef(file: AttachmentRef, mime?: string | null): boolean {
  const type = mime ?? file.mime_type ?? file.file?.type
  if (type?.startsWith('image/')) return true
  return /\.(png|jpe?g|gif|webp)$/i.test(file.name)
}

function isPdfRef(file: AttachmentRef, mime?: string | null): boolean {
  const type = mime ?? file.mime_type ?? file.file?.type
  return type === 'application/pdf' || /\.pdf$/i.test(file.name)
}

function canPreviewLocal(file: File): boolean {
  return (
    file.type.startsWith('image/') ||
    file.type === 'application/pdf' ||
    /\.(png|jpe?g|gif|webp|pdf)$/i.test(file.name)
  )
}

/** Blob URLs ignore PDF hash params in Chrome and often paint a blank viewer. */
function pdfEmbedSrc(url: string): string {
  if (url.startsWith('blob:')) return url
  return `${url}#page=1&toolbar=0&navpanes=0&scrollbar=0&view=FitH`
}

function useObjectUrlRef<T extends HTMLImageElement | HTMLIFrameElement>(
  file?: File
) {
  const objectUrl = useRef<string | null>(null)

  return useCallback(
    (element: T | null) => {
      if (objectUrl.current) {
        URL.revokeObjectURL(objectUrl.current)
        objectUrl.current = null
      }

      if (element && file) {
        const nextUrl = URL.createObjectURL(file)
        objectUrl.current = nextUrl
        element.src = nextUrl
      }
    },
    [file]
  )
}

function LocalImageThumb({
  file,
  label,
  className,
}: {
  file: File
  label: string
  className?: string
}) {
  const imageRef = useObjectUrlRef<HTMLImageElement>(file)

  return <img ref={imageRef} alt={label} className={className} />
}

function PdfThumb({
  url,
  file,
  label,
  variant,
  className,
}: {
  url?: string
  file?: File
  label: string
  variant: 'compact' | 'wide'
  className?: string
}) {
  const iframeRef = useObjectUrlRef<HTMLIFrameElement>(file)

  return (
    <iframe
      ref={iframeRef}
      src={!file && url ? pdfEmbedSrc(url) : undefined}
      title={label}
      className={cn(
        'absolute left-0 top-0 origin-top-left border-0 bg-white',
        variant === 'wide'
          ? 'h-[320px] w-[460px] scale-[0.66]'
          : 'h-[240px] w-[360px] scale-[0.56]',
        className
      )}
    />
  )
}

export interface AttachmentPreviewGridProps {
  items: AttachmentRef[]
  removeLabel: string
  onRemove?: (item: AttachmentRef) => void
  onRename?: (item: AttachmentRef, name: string) => Promise<void> | void
  disabled?: boolean
  onItemClick?: (item: AttachmentRef) => void
  itemClickLabel?: string
  cardVariant?: 'compact' | 'wide'
  showTypeBadge?: boolean
  typeBadgeClassName?: string
  metadataClassName?: string
  metadataLayout?: 'inline-date-size' | 'stacked-type-size'
}

export function AttachmentPreviewGrid({
  items,
  removeLabel,
  onRemove,
  onRename,
  disabled,
  onItemClick,
  itemClickLabel,
  cardVariant = 'compact',
  showTypeBadge = true,
  typeBadgeClassName,
  metadataClassName,
  metadataLayout = 'inline-date-size',
}: AttachmentPreviewGridProps) {
  const { t } = useTranslation('shared')
  const renameSubmissionInFlight = useRef(false)
  const [editingKey, setEditingKey] = useState<string | null>(null)
  const [renameDraft, setRenameDraft] = useState('')
  const [renameError, setRenameError] = useState<string | null>(null)
  const [renaming, setRenaming] = useState(false)
  const previews = useMemo(
    () =>
      items.map(item => {
        const localType = item.file?.type
        const localPreviewFile =
          item.file && canPreviewLocal(item.file) ? item.file : null

        return {
          item,
          localPreviewFile,
          previewUrl: localPreviewFile ? null : (item.url ?? null),
          mime: localType || item.mime_type,
        }
      }),
    [items]
  )

  if (items.length === 0) return null

  const isWide = cardVariant === 'wide'

  const itemKey = (item: AttachmentRef) =>
    item.id ?? `${item.source}:${item.name}:${item.url ?? ''}`

  const validateRename = (item: AttachmentRef, value = renameDraft) => {
    const result = validateDocumentRename(
      value,
      fileNameExtension(item.name),
      NAME_MAX
    )
    if (result === 'required') return t('attachFiles.renameRequired')
    if (result === 'invalid') return t('attachFiles.renameInvalid')
    if (result === 'tooLong') return t('attachFiles.nameMax', { max: NAME_MAX })
    return null
  }

  const beginRename = (item: AttachmentRef) => {
    renameSubmissionInFlight.current = false
    setEditingKey(itemKey(item))
    setRenameDraft(fileNameBase(item.name))
    setRenameError(null)
  }

  const cancelRename = () => {
    renameSubmissionInFlight.current = false
    setEditingKey(null)
    setRenameDraft('')
    setRenameError(null)
    setRenaming(false)
  }

  const saveRename = async (item: AttachmentRef) => {
    if (!onRename || renameSubmissionInFlight.current) return
    const error = validateRename(item)
    setRenameError(error)
    if (error) return

    const name = renameDraft.trim()
    if (name === fileNameBase(item.name)) {
      cancelRename()
      return
    }

    renameSubmissionInFlight.current = true
    setRenaming(true)
    try {
      await onRename(item, name)
      cancelRename()
    } catch (error) {
      renameSubmissionInFlight.current = false
      setRenaming(false)
      setRenameError(
        getApiFieldError(error, 'name') ?? t('attachFiles.renameError')
      )
    }
  }

  const handleCardKeyDown = (
    event: KeyboardEvent<HTMLDivElement>,
    item: AttachmentRef
  ) => {
    if (!onItemClick) return
    if (event.key !== 'Enter' && event.key !== ' ') return
    event.preventDefault()
    onItemClick(item)
  }

  return (
    <div className='flex flex-wrap items-start gap-4 pt-4'>
      {previews.map(({ item, localPreviewFile, previewUrl, mime }) => {
        const key = itemKey(item)
        const isEditing = editingKey === key
        const isClickable =
          !isEditing && !!onItemClick && (!!item.url || !!item.file)
        const canRename = !!onRename && item.source === 'upload'
        const size =
          typeof item.size === 'number'
            ? item.size
            : typeof item.file?.size === 'number'
              ? item.file.size
              : null
        const dateSource =
          item.created_at ??
          (item.file ? new Date(item.file.lastModified).toISOString() : null)
        const dateLabel = dateSource ? formatDate(dateSource) : null
        const typeLabel = formatDocumentFileType(item.name, mime)
        const mimeLabel = mime || typeLabel
        const image = isImageRef(item, mime)
        const pdf = isPdfRef(item, mime)

        return (
          <div
            key={key}
            role={isClickable ? 'button' : undefined}
            tabIndex={isClickable ? 0 : undefined}
            aria-label={isClickable ? (itemClickLabel ?? item.name) : undefined}
            onClick={() => {
              if (isClickable) onItemClick?.(item)
            }}
            onKeyDown={event => {
              if (isClickable) handleCardKeyDown(event, item)
            }}
            className={cn(
              'overflow-hidden border border-[#e7e9ef] bg-white shadow-[0_1px_4px_rgba(38,45,66,0.1),0_1px_4px_rgba(38,45,66,0.05)]',
              isWide
                ? 'w-full shrink-0 rounded-3xl sm:w-[calc(50%-0.5rem)] sm:max-w-[420px]'
                : 'w-[160px] shrink-0 rounded-2xl',
              isClickable &&
                'cursor-pointer transition-shadow hover:shadow-[0_8px_24px_rgba(38,45,66,0.14)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#ff5a1f] focus-visible:ring-offset-2'
            )}
          >
            <div
              className={cn(
                'relative w-full overflow-hidden',
                isWide
                  ? pdf
                    ? 'bg-white'
                    : 'bg-[#eef0f5]'
                  : localPreviewFile || previewUrl
                    ? 'bg-[#8f95a6]'
                    : 'bg-[#eef0f5]',
                isWide ? 'h-[230px]' : 'h-[120px]'
              )}
            >
              {localPreviewFile && image ? (
                <LocalImageThumb
                  file={localPreviewFile}
                  label={item.name}
                  className={cn(
                    'absolute inset-0 size-full object-cover',
                    !isWide && 'opacity-80'
                  )}
                />
              ) : previewUrl && image ? (
                <img
                  src={previewUrl}
                  alt={item.name}
                  className={cn(
                    'absolute inset-0 size-full object-cover',
                    !isWide && 'opacity-80'
                  )}
                />
              ) : (localPreviewFile || previewUrl) && pdf ? (
                <div
                  className={cn(
                    'absolute inset-y-0 left-0 overflow-hidden',
                    isWide ? 'bg-white' : 'bg-[#8f95a6]',
                    onRemove && isWide ? 'right-[56px]' : 'right-0'
                  )}
                >
                  <PdfThumb
                    url={previewUrl ?? undefined}
                    file={localPreviewFile ?? undefined}
                    label={item.name}
                    variant={cardVariant}
                    className={cn(!isWide && 'opacity-80')}
                  />
                </div>
              ) : (
                <div className='flex size-full items-center justify-center text-[#6c7285]'>
                  <FileText className={cn(isWide ? 'size-8' : 'size-6')} />
                </div>
              )}

              {onRemove && (
                <button
                  type='button'
                  disabled={disabled}
                  onClick={event => {
                    event.stopPropagation()
                    onRemove(item)
                  }}
                  className={cn(
                    'absolute z-10 inline-flex items-center justify-center overflow-hidden bg-white disabled:opacity-50',
                    isWide
                      ? 'right-3 top-3 size-12 rounded-full text-[#555b6a] shadow-[0_8px_20px_rgba(17,24,39,0.12)]'
                      : 'right-3 top-3 size-6 rounded-lg text-[#272c36]'
                  )}
                  aria-label={removeLabel}
                >
                  <X
                    className={cn(isWide ? 'size-6' : 'size-4')}
                    strokeWidth={isWide ? 2 : 2.4}
                  />
                </button>
              )}

              {showTypeBadge && (
                <span
                  className={cn(
                    'absolute bottom-3 left-4 inline-flex h-6 items-center rounded-lg bg-[#8f95a6] px-2 py-1 text-xs font-medium leading-4 text-white',
                    typeBadgeClassName
                  )}
                >
                  {typeLabel}
                </span>
              )}
            </div>

            <div
              className={cn(
                'flex flex-col border-t border-[#e7e9ef]',
                isWide ? 'gap-2 px-5 py-4' : 'gap-1 px-4 py-3'
              )}
            >
              {isEditing ? (
                <div
                  className='flex min-w-0 flex-col gap-1'
                  onClick={event => event.stopPropagation()}
                >
                  <div
                    className={cn(
                      'flex h-7 min-w-0 items-center overflow-hidden rounded-md border bg-white px-2 text-sm font-medium leading-[1.4]',
                      renameError
                        ? 'border-[1.5px] border-[#d63b33] shadow-[0_0_0_2px_#fff1ea]'
                        : 'border-[#e7e9ef] focus-within:border-[#ff5a1f]'
                    )}
                  >
                    <input
                      autoFocus
                      value={renameDraft}
                      disabled={renaming}
                      onChange={event => {
                        const value = event.target.value
                        setRenameDraft(value)
                        setRenameError(validateRename(item, value))
                      }}
                      onFocus={event => event.currentTarget.select()}
                      onBlur={() => {
                        if (!renameSubmissionInFlight.current) cancelRename()
                      }}
                      onKeyDown={event => {
                        event.stopPropagation()
                        if (event.key === 'Escape') {
                          event.preventDefault()
                          cancelRename()
                        } else if (event.key === 'Enter') {
                          event.preventDefault()
                          void saveRename(item)
                        }
                      }}
                      aria-invalid={!!renameError}
                      aria-describedby={
                        renameError
                          ? `attachment-rename-error-${key}`
                          : undefined
                      }
                      aria-label={t('attachFiles.renameLabel')}
                      className='min-w-0 flex-1 border-0 bg-transparent p-0 text-sm font-medium leading-[1.4] text-[#171a20] outline-none disabled:opacity-60'
                    />
                    <span className='shrink-0'>
                      {fileNameExtension(item.name)}
                    </span>
                  </div>
                  {renameError ? (
                    <p
                      id={`attachment-rename-error-${key}`}
                      className='text-xs font-normal leading-4 text-[#991b1b]'
                    >
                      {renameError}
                    </p>
                  ) : null}
                </div>
              ) : (
                <div className='flex min-w-0 items-center gap-2'>
                  <TruncatedTextTooltip
                    content={item.name}
                    closeOnPointerDown
                    className={cn(
                      'min-w-0 truncate font-medium leading-[1.4] text-[#171a20]',
                      isWide ? 'text-[16px]' : 'text-sm'
                    )}
                  >
                    {item.name}
                  </TruncatedTextTooltip>
                  {canRename ? (
                    <button
                      type='button'
                      disabled={disabled || renaming}
                      onClick={event => {
                        event.stopPropagation()
                        beginRename(item)
                      }}
                      className='shrink-0 rounded-md text-[#ff5a1f] hover:text-[#e6511c] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#ff5a1f] disabled:opacity-50'
                      aria-label={t('attachFiles.editName')}
                    >
                      <Pencil className='size-4' />
                    </button>
                  ) : null}
                </div>
              )}
              {metadataLayout === 'stacked-type-size' ? (
                <>
                  <p
                    className={cn(
                      'truncate text-xs leading-[1.4] text-[#6c7285]',
                      metadataClassName
                    )}
                  >
                    {mimeLabel}
                  </p>
                  {(size != null || dateLabel) && (
                    <p
                      className={cn(
                        'text-xs leading-[1.4] text-[#6c7285]',
                        metadataClassName
                      )}
                    >
                      {size != null ? formatAttachmentSize(size) : dateLabel}
                    </p>
                  )}
                </>
              ) : (
                <p
                  className={cn(
                    'flex items-center gap-1 text-xs font-normal leading-4 text-[#3d434e]',
                    metadataClassName
                  )}
                >
                  {dateLabel && <span>{dateLabel}</span>}
                  {dateLabel && size != null && (
                    <span
                      className='inline-block size-1 shrink-0 rounded-full bg-[#8f95a6]'
                      aria-hidden
                    />
                  )}
                  {size != null && <span>{formatAttachmentSize(size)}</span>}
                </p>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default AttachmentPreviewGrid
